import React, { useState, useRef, useCallback } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Typography,
    Box,
} from "@mui/material";
import { FolderSpecial as ProjectIcon } from "@mui/icons-material";
import ProjectService from "../../services/Project.service";

/**
 * Type-to-search autocomplete for picking an existing project.
 * Only fetches when the user types 2+ characters (debounced 250 ms).
 */
export default function ProjectPicker({
    value,
    onChange,
    label = "Project",
    disabled = false,
    error = "",
    size = "medium",
}) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);

    const handleInputChange = useCallback((_, inputVal, reason) => {
        clearTimeout(timerRef.current);

        if (reason !== "input") {
            setLoading(false);
            return;
        }

        const term = (inputVal || "").trim();
        setOptions([]);

        if (term.length < 2) {
            setLoading(false);
            return;
        }

        setLoading(true);
        timerRef.current = setTimeout(async () => {
            try {
                const res = await ProjectService.search(term);
                const results = Array.isArray(res.data)
                    ? res.data
                    : (res.data?.results ?? []);
                setOptions(results);
            } catch {
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 250);
    }, []);

    const getLabel = (opt) => {
        if (!opt) return "";
        return `${opt.number || opt.project_number || ""} ${opt.name || ""}`.trim();
    };

    return (
        <Autocomplete
            options={options}
            value={value}
            onChange={(_, newVal) => onChange(newVal)}
            onInputChange={handleInputChange}
            filterOptions={(x) => x}
            getOptionLabel={getLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            loading={loading}
            disabled={disabled}
            noOptionsText="Type at least 2 characters to search…"
            renderOption={({ key, ...optProps }, opt) => (
                <Box
                    key={key}
                    component="li"
                    {...optProps}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <ProjectIcon
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                    />
                    <Typography variant="body2">
                        {opt.number || opt.project_number} {opt.name}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    error={Boolean(error)}
                    helperText={error || ""}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
